import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { EditDescriptionDialogProps, validateDescription, DESCRIPTION_MIN_CHARS, DESCRIPTION_MAX_CHARS } from "../types";

export const EditDescriptionDialog = ({
  isOpen,
  onOpenChange,
  description,
  onSave,
}: EditDescriptionDialogProps) => {
  const [tempDescription, setTempDescription] = useState(description);
  const validation = validateDescription(tempDescription);

  const handleSave = () => {
    if (validation.isValid) {
      onSave(tempDescription);
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setTempDescription(description); // Reset to original
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Redigera jobbeskrivning</DialogTitle>
          <DialogDescription>
            Uppdatera den AI-genererade jobbeskrivningen med korrekta detaljer. 
            Använd formateringsverktygen för att strukturera informationen tydligt. 
            Kvalificerande frågor kommer att genereras om på nytt baserat på den nya beskrivningen.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                    Granska AI-genererat innehåll
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Kontrollera innehållet noga</strong> och korrigera eventuella fel eller missade detaljer. 
                    AI:n kan ha gjort antaganden som inte stämmer för er specifika roll eller organisation.
                  </p>
                </div>
              </div>
            </div>

            <RichTextEditor
              content={tempDescription}
              onChange={setTempDescription}
              placeholder="Redigera och förbättra den AI-genererade jobbeskrivningen..."
              minCharacters={DESCRIPTION_MIN_CHARS}
              maxCharacters={DESCRIPTION_MAX_CHARS}
              className="w-full"
            />

            {!validation.isValid && (
              <p className="text-sm text-red-500">
                {validation.error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleClose}
              >
                Avbryt
              </Button>
              <Button
                onClick={handleSave}
                disabled={!validation.isValid}
                className="flex items-center gap-2"
              >
                Spara ändringar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditDescriptionDialog; 